import { Form, Typography } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import styled from "styled-components";

import { ActionBar } from "../../../ActionBar/ActionBar";
import type { AntCustomFormField } from "../../types/types";
import { CsvTerminalsImport } from "../CsvTerminalsImport/CsvTerminalsImport";
import { Textarea } from "../Textarea/Textarea";

const { Text } = Typography;

export interface TerminalsTextareaProps extends AntCustomFormField<string> {
    readOnly?: boolean;
    tableFieldName: string;
}

/**
 * Textarea component for textareaTerminalsInput that syncs with the terminals table on blur.
 * When user types terminal IDs and leaves the field, it updates the corresponding table.
 */
export const TerminalsTextarea: FC<TerminalsTextareaProps> = ({ value, onChange, readOnly, tableFieldName }) => {
    const form = Form.useFormInstance();

    const handleBlur = () => {
        if (value) {
            // Parse the textarea content - split by newlines and/or commas
            const terminalIds = value
                .split(/[\n,]+/)
                .map((id: string) => id.trim())
                .filter((id: string) => id.length > 0);

            // Update both the textarea field (normalize format) and the terminals table field
            if (terminalIds.length > 0) {
                const content = terminalIds.join("\n");
                if (onChange) {
                    onChange(content);
                }
                form.setFieldValue(tableFieldName, {
                    terminalIdList: terminalIds,
                    areAllTerminalsSelected: false,
                });
            }
        } else if (!value || value === "") {
            if (onChange) {
                onChange("");
            }
            form.setFieldValue(tableFieldName, {
                terminalIdList: [],
                areAllTerminalsSelected: false,
            });
        }
    };

    const targetField = `textareaTerminalsInput${tableFieldName.slice(-1)}`;

    return (
        <>
            <ActionBar className="column">
                <CsvTerminalsImport readOnly={readOnly} targetField={targetField} />

                <Textarea value={value} onChange={onChange} readOnly={readOnly || false} onBlur={handleBlur} />
            </ActionBar>
            <StyledText type="secondary">{i18n.t("campaignCreate.terminalsFormatHint")}</StyledText>
        </>
    );
};

const StyledText = styled(Text)`
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 8px;
    display: block;
`;
